import * as THREE from 'three';
import { SENDA } from '@somnhold/content';
import { cartPoint, cartsLayout, drillsLayout, drillTurnsRight, hexToWorld, strutsLayout, wallForce, type BattleState, type CartsLayout, type DrillsLayout, type MapDef, type StrutsLayout } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The eyes (docs/sendas/artifice.md, chapter 4, «Los ojos»), for the artificer's path.
 *
 * - `gradaThaw` (act 1, the memory, and its dawn scene): Grada's ledge in the Marea of the Thaw, by night under a cold
 *   moon (at dawn for the scene; the memory's grey is the canvas's filter). The cliff's wall along the north, slate in
 *   courses, the quarrymen's houses cut into it, each a door with a warm lantern (one goes out as the Marea gets in);
 *   over them the great stair, broken by the ice, its landing hanging over the ledge from four timber struts that
 *   creak: loose they shake, held by a golem they stand still, wedged they get pale wedges driven in (Bruni's work
 *   growing), and Grillo's lamp shows the loosest with a red beat at its foot. Meltwater runs down the wall in channels
 *   and freezes in icicles under the landing; the Raja a thin line high up. Along the south the drop, mist over it.
 * - `iceMine` (act 2): the dwarven mine in the glacier, a gallery between walls of blue ice and dark rock with cold
 *   lamps; three rails across it, timber sleepers and iron, each coming down its side slope (the rail climbing it) from
 *   where its cart froze. The carts: crusted with frost at the top, rolling down their rail, tipped at the buffer once
 *   spent, a frame of new planks at the top while the next is built. Each brake a lever with a red grip. The beetles
 *   still in their shells glint with frost. The ice's cracks coming (pale discs). The drill hall's door at the top.
 * - `drillHall` (act 3, the test): a hall cut in the ice. The mill at the mouth (its sails turn while the shaft runs,
 *   slower with more asked of it, still when it jams); the shaft along the roof; the belts down to the four drills, two
 *   by each face of a block of ice with the geode inside (blue, glowing through). Each drill a frame with its auger
 *   aimed at the ice, its force painted big on a plate and its arrow (the way it cuts) in red; in, it turns (its arrow's
 *   way if its belt says so, else backwards: no chips then, a red glint); its clutch lever up (out) or down (in). The
 *   belts' station: belts I and II, straight or crossed as they are. The walls crack as they are cut and come down in
 *   blocks; the geode shows. La Barrena's slash (an arc on the floor) and its spin (a ring over all the hall).
 * - `mineCamp` (the campfire): a gallery of the mine by an overturned cart, the fire warm against the blue.
 *
 * Draw calls: the dressing (one merged mesh), its far ice (one more), the bright bits (one), the struts, carts, drills,
 * walls' chunks and levers as small groups, the discs (a pool of eight), the glow layer, the sky. No lights of its own:
 * the board's are repainted. The sim's events and state are read once a tick; frames only move what is there.
 */

export const OJOS_PLACES = new Set(['gradaThaw', 'iceMine', 'drillHall', 'mineCamp']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const WARM = new THREE.Color('#ffb48a');
const COLD = new THREE.Color('#9fdcff');
const WHITE = new THREE.Color('#ffffff');
const SLATE = ['#5d6672', '#535b67', '#68717d', '#4a515c'];
const ICE = ['#7fb3d1', '#6aa0c2', '#93c4de', '#5a8db0'];
const ROCKB = ['#3b4350', '#333a46', '#444d5a', '#2c323c'];
const WOOD = ['#8c6a4c', '#7a5c42', '#a27c5a'];
const IRON = '#4a5058';

type Sky = 'day' | 'dusk' | 'night' | 'dawn';

const LOOK: Record<string, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number] }> = {
  night: { bg: '#2a3346', fog: ['#2f394d', 28, 90], hemi: ['#a9bad6', '#3c3a46', 1.9], key: ['#c4d4f4', 1.7], rim: ['#ffb07a', 0.9] },
  dawn: { bg: '#6a7488', fog: ['#717b8f', 30, 110], hemi: ['#d2d8e4', '#3a3a44', 1.7], key: ['#ffe0cc', 2.2], rim: ['#b0c4ee', 1.3] },
  ice: { bg: '#1e2a38', fog: ['#22303f', 24, 80], hemi: ['#cfe6f6', '#2f3a48', 2.2], key: ['#e8f4ff', 1.8], rim: ['#7fc4ff', 1.4] },
  camp: { bg: '#1a222e', fog: ['#1e2834', 20, 70], hemi: ['#a9c4dc', '#3a2c26', 1.6], key: ['#ffd0b0', 1.3], rim: ['#7fb4ff', 1.0] },
};

/** A number painted on a plate: pale on dark iron, big (a drill's force). */
const numTex = (text: string): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = '#1b1f25';
    g.fillRect(0, 0, 128, 128);
    g.strokeStyle = '#9aa3ae';
    g.lineWidth = 8;
    g.strokeRect(6, 6, 116, 116);
    g.fillStyle = '#ffffff';
    g.font = '800 92px Georgia, serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText(text, 64, 70);
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** The way a drill cuts: a curved red arrow round its axis on transparent. */
const arrowTex = (): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    g.strokeStyle = '#ff4f3a';
    g.lineWidth = 14;
    g.lineCap = 'round';
    g.beginPath();
    g.arc(64, 64, 40, Math.PI * 0.15, Math.PI * 1.55);
    g.stroke();
    g.fillStyle = '#ff4f3a';
    const a = Math.PI * 1.55;
    const x = 64 + Math.cos(a) * 40;
    const y = 64 + Math.sin(a) * 40;
    g.beginPath();
    g.moveTo(x + 24, y - 2);
    g.lineTo(x - 6, y - 20);
    g.lineTo(x - 4, y + 20);
    g.closePath();
    g.fill();
  }
  const t = new THREE.CanvasTexture(c);
  t.anisotropy = 4;
  return t;
};

/** A soft round glow. */
const glowTex = (inner: string, outer: string): THREE.Texture => {
  const c = typeof document !== 'undefined' ? document.createElement('canvas') : null;
  if (!c) return new THREE.Texture();
  c.width = c.height = 128;
  const g = c.getContext('2d');
  if (g) {
    const gr = g.createRadialGradient(64, 64, 4, 64, 64, 64);
    gr.addColorStop(0, inner);
    gr.addColorStop(1, outer);
    g.fillStyle = gr;
    g.fillRect(0, 0, 128, 128);
  }
  return new THREE.CanvasTexture(c);
};

interface StrutView {
  g: THREE.Group;
  wedges: THREE.Mesh[];
  at: THREE.Vector3;
  shake: number;
  flash: number;
}
interface CartView {
  g: THREE.Group;
  frost: THREE.Mesh;
  wheels: THREE.Mesh[];
  frame: THREE.Group;
  pos: THREE.Vector3;
  spin: number;
}
interface DrillView {
  g: THREE.Group;
  auger: THREE.Group;
  lever: THREE.Group;
  belt: THREE.Group;
  tip: THREE.Vector3;
  spin: number;
  flick: number;
}

export class OjosLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow = new GlowLayer(320);
  private dome: SkyDome | null = null;
  private now = 0;
  private outside: boolean;
  private sky: Sky = 'night';
  private open = new Set<string>();
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number; out?: boolean }[] = [];
  private last: BattleState | null = null;
  private v3 = new THREE.Vector3();
  private discs: { mesh: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  private discColor = COLD;
  // Grada.
  private G: StrutsLayout | null = null;
  private struts: StrutView[] = [];
  private landing: THREE.Group | null = null;
  private jolt = 0;
  private houseLamps: number[] = [];
  private houseAt: THREE.Vector3[] = [];
  // The mine.
  private M: CartsLayout | null = null;
  private carts: CartView[] = [];
  private brakes: THREE.Group[] = [];
  private brakePull: number[] = [];
  private slopeY: number[][] = [];
  // The drill hall.
  private D: DrillsLayout | null = null;
  private drills: DrillView[] = [];
  private mill: THREE.Group | null = null;
  private millSpin = 0;
  private wallChunks: { mesh: THREE.Mesh; home: THREE.Vector3; fall: THREE.Vector3; spin: number }[][] = [[], []];
  private crackMats: THREE.MeshBasicMaterial[] = [];
  private geode: THREE.Vector3 | null = null;
  private geodeMat: THREE.MeshBasicMaterial | null = null;
  private beltViews: { straight: THREE.Group; crossed: THREE.Group; lever: THREE.Group }[] = [];
  private arc: THREE.Mesh | null = null;
  private arcMat: THREE.MeshBasicMaterial | null = null;
  private spinRing: THREE.Mesh | null = null;
  private spinMat: THREE.MeshBasicMaterial | null = null;
  private jamFlash = 0;
  private wallFlash = [0, 0];
  private hallC = new THREE.Vector3();

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `ojos-${place}`;
    this.outside = place === 'gradaThaw';
    const kit = new Kit();
    const far = new Kit();
    const hot = new Kit();
    if (place === 'gradaThaw') this.buildGrada(kit, far, hot, s);
    else if (place === 'iceMine') this.buildMine(kit, far, hot, s);
    else if (place === 'drillHall') this.buildHall(kit, far, hot, s);
    else this.buildCamp(kit, far, hot);
    const mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.82, metalness: 0.06, flatShading: true });
    this.mats.push(mat);
    const dress = kit.build(mat, 'ojos-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    const farMesh = far.build(mat, 'ojos-far');
    if (farMesh) {
      this.geos.push(farMesh.geometry);
      this.group.add(farMesh);
    }
    const hotMat = new THREE.MeshBasicMaterial({ vertexColors: true, fog: false });
    this.mats.push(hotMat);
    const hotMesh = hot.build(hotMat, 'ojos-hot');
    if (hotMesh) {
      this.geos.push(hotMesh.geometry);
      this.group.add(hotMesh);
    }
    this.discPool();
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry | THREE.Texture>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else if (x instanceof THREE.Texture) this.texs.push(x);
    else this.geos.push(x);
    return x;
  }

  private std(color: string, o: THREE.MeshStandardMaterialParameters = {}): THREE.MeshStandardMaterial {
    return this.track(new THREE.MeshStandardMaterial({ color, roughness: 0.8, metalness: 0.1, flatShading: true, ...o }));
  }

  private hex(q: number, r: number, h = 0): THREE.Vector3 {
    const c = hexToWorld(q, r);
    return this.ctx.at(c.x, c.y, h);
  }

  private light(p: THREE.Vector3, color: THREE.ColorRepresentation, size: number, a: number, flick = 0): number {
    this.lights.push({ p: p.clone(), c: new THREE.Color(color), size, a, seed: this.lights.length * 1.37, flick });
    return this.lights.length - 1;
  }

  private setOpen(cells: { q: number; r: number }[]): void {
    for (const h of cells) this.open.add(`${h.q},${h.r}`);
  }

  private voidAt = (x: number, z: number): boolean => {
    if (!this.open.size) return false;
    const hr = Math.round(z / (S * 0.866));
    const hq = Math.round(x / S - hr / 2);
    for (const [dq, dr] of [
      [0, 0],
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const) {
      const k = `${hq + dq},${hr + dr}`;
      if (!this.open.has(k)) continue;
      const c = hexToWorld(hq + dq, hr + dr);
      if ((x - c.x * K) ** 2 + (z - c.y * K) ** 2 < (0.62 * S) ** 2) return false;
    }
    return true;
  };

  private carve(terrain: TerrainBuild): void {
    const mesh = terrain.mesh;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const mw = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const idx = geo.index;
    const tri = (i: number): number => (idx ? idx.getX(i) : i);
    const n = idx ? idx.count : pos.count;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
        cx += v.x;
        cz += v.z;
      }
      const out = this.voidAt(cx / 3, cz / 3);
      if (idx) {
        if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
      } else if (out) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        pos.setXYZ(i + 1, x, y, z);
        pos.setXYZ(i + 2, x, y, z);
      }
    }
    if (idx) geo.setIndex(keep);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
    terrain.decor.traverse((o) => {
      if (o.name === 'outskirts-ground' || o.name === 'roads') o.visible = false;
    });
  }

  private underfloor(kit: Kit, y: number, color: string): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    kit.add(box, color, place(0, y - 0.25, 0, 0, new THREE.Vector3(80, 0.5, 80)));
    box.dispose();
  }

  private rock(kit: Kit, x: number, z: number, top: number, w: number, k: number, colors: string[]): void {
    const dode = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const j = Math.sin(x * 12.9 + z * 7.3) * 0.5 + 0.5;
    kit.add(dode, colors[Math.floor(j * 3.99) % colors.length] as string, place(x, top * 0.5 - 0.2, z, j * 3 + k, new THREE.Vector3(w, top + 0.4, w * (0.8 + j * 0.4))));
    if (j > 0.4) kit.add(box, colors[(Math.floor(j * 7) + 1) % colors.length] as string, place(x + 0.2, top * 0.75, z - 0.15, j * 5, new THREE.Vector3(w * 0.5, top * 0.5, w * 0.45), 0.2, -0.15));
    dode.dispose();
    box.dispose();
  }

  private discPool(): void {
    const geo = this.track(new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2));
    for (let i = 0; i < 8; i++) {
      const mat = this.track(new THREE.MeshBasicMaterial({ color: COLD, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
      const mesh = new THREE.Mesh(geo, mat);
      mesh.visible = false;
      mesh.renderOrder = 3;
      this.group.add(mesh);
      this.discs.push({ mesh, mat });
    }
  }

  /**
   * Walls round the open cells: every closed cell next to an open one is a block of its kind (`ice`: blue ice and dark
   * rock in turns; else slate in courses), taller further from the floor; a second ring behind, higher and darker.
   */
  private wallsRound(kit: Kit, far: Kit, hot: Kit, ice: boolean, height: number): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const near: { q: number; r: number }[] = [];
    const ring2: { q: number; r: number }[] = [];
    const R = this.map.radius;
    const ns = [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [1, -1],
      [-1, 1],
    ] as const;
    const isOpen = (q: number, r: number): boolean => this.open.has(`${q},${r}`);
    for (let r = -R; r <= R; r++)
      for (let q = -R; q <= R; q++) {
        if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) > R || isOpen(q, r)) continue;
        const n1 = ns.some(([dq, dr]) => isOpen(q + dq, r + dr));
        if (n1) near.push({ q, r });
        else if (ns.some(([dq, dr]) => ns.some(([eq, er]) => isOpen(q + dq + eq, r + dr + er)))) ring2.push({ q, r });
      }
    near.forEach((h, i) => {
      const p = this.hex(h.q, h.r, 0);
      const j = Math.sin(h.q * 7.7 + h.r * 3.1) * 0.5 + 0.5;
      const hgt = height + j * 0.9;
      if (ice) {
        const col = (i + h.r) % 3 === 0 ? (ROCKB[Math.floor(j * 3.99)] as string) : (ICE[Math.floor(j * 3.99)] as string);
        kit.add(box, col, place(p.x, hgt / 2 - 0.05, p.z, j * 2, new THREE.Vector3(S * 1.02, hgt, S * 1.02), (j - 0.5) * 0.08, (j - 0.5) * 0.08));
        // Icicles and frost on the wall's lip; a glint of bright ice now and then.
        kit.add(box, '#b8dcef', place(p.x, hgt + 0.04, p.z, j, new THREE.Vector3(S * 0.9, 0.1, S * 0.9)));
        if (j > 0.62) hot.add(box, '#bfe9ff', place(p.x + 0.2, hgt * 0.55, p.z + 0.35, j * 3, new THREE.Vector3(0.05, hgt * 0.5, 0.05), 0.1, 0.2));
      } else {
        kit.add(box, SLATE[(i + h.r) % 4] as string, place(p.x, hgt / 2 - 0.05, p.z, 0, new THREE.Vector3(S * 1.0, hgt, S * 1.0)));
        kit.add(box, '#3f4650', place(p.x, hgt + 0.05, p.z, 0, new THREE.Vector3(S * 1.05, 0.1, S * 1.05)));
      }
    });
    for (const h of ring2) {
      const p = this.hex(h.q, h.r, 0);
      const j = Math.sin(h.q * 3.3 + h.r * 9.1) * 0.5 + 0.5;
      this.rock(far, p.x, p.z, height + 1.4 + j * 1.8, S * 1.3, h.q, ice ? ['#4d7a98', '#3f6884', '#5a8aa8', '#355a74'] : ['#3c434d', '#343a43', '#47505b', '#2e343c']);
    }
    box.dispose();
  }

  /* ---------- Grada's ledge ---------- */

  private buildGrada(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = strutsLayout(s.seed);
    this.G = L;
    this.setOpen(L.floor);
    this.underfloor(far, -6, '#1c222c');
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
    // The cliff's wall: courses of slate rising behind the houses, ledges with more houses higher up.
    const wz = this.hex(0, -5).z - 0.7;
    for (let x = -14; x <= 14; x += 1.5) {
      const j = Math.sin(x * 12.9) * 0.5 + 0.5;
      far.add(box, SLATE[Math.floor(j * 3.99)] as string, place(x, 7, wz - 0.8 - j * 0.8, 0, new THREE.Vector3(1.7, 16, 2.0)));
    }
    for (let li = 0; li < 2; li++) {
      const ly = 3.4 + li * 2.8;
      const lz = wz + 0.1;
      far.add(box, '#4b525c', place(0, ly - 0.3, lz, 0, new THREE.Vector3(22, 0.5, 1.6)));
      for (let i = 0; i < 7; i++) {
        const hx = -8 + i * 2.6 + li * 1.1;
        far.add(box, SLATE[(i + li) % 4] as string, place(hx, ly + 0.5, lz - 0.2, 0, new THREE.Vector3(1.4, 1.0, 1.0)));
        hot.add(box, '#c87a48', place(hx + 0.3, ly + 0.55, lz + 0.32, 0, new THREE.Vector3(0.14, 0.16, 0.03)));
        this.light(new THREE.Vector3(hx + 0.3, ly + 0.55, lz + 0.5), WARM, 0.8, 0.32, 0.6);
      }
    }
    // The Raja: a thin dark line high on the wall, a little red deep in it.
    for (let x = -12; x < 12; x += 0.8) {
      const yy = 11 + Math.sin(x * 1.9) * 0.4 + Math.sin(x * 0.37) * 0.8;
      kit.add(box, '#0a0b0e', place(x, yy, wz + 0.25, 0, new THREE.Vector3(0.95, 0.12 + (Math.sin(x) * 0.5 + 0.5) * 0.08, 0.2), 0, Math.cos(x * 1.9) * 0.4));
    }
    // The houses cut into the wall at the ledge: a facade of dressed stone, a dark doorway, a warm lantern, a slate eave.
    L.houses.forEach((hh, i) => {
      const p = this.hex(hh.q, hh.r, 0);
      kit.add(box, SLATE[(i + 1) % 4] as string, place(p.x, 1.2, p.z + 0.2, 0, new THREE.Vector3(S * 1.55, 2.4, 0.8)));
      kit.add(box, '#1a1d22', place(p.x - 0.1, 0.7, p.z + 0.62, 0, new THREE.Vector3(0.62, 1.3, 0.06)));
      kit.add(box, '#7a5c42', place(p.x - 0.1, 0.7, p.z + 0.64, 0, new THREE.Vector3(0.56, 1.22, 0.04)));
      kit.add(box, '#38404b', place(p.x, 2.5, p.z + 0.5, 0, new THREE.Vector3(S * 1.7, 0.14, 0.8), 0.25, 0));
      hot.add(box, '#ffd2a6', place(p.x + 0.45, 1.0, p.z + 0.66, 0, new THREE.Vector3(0.24, 0.22, 0.04)));
      kit.add(box, IRON, place(p.x + 0.45, 1.25, p.z + 0.66, 0, new THREE.Vector3(0.3, 0.05, 0.08)));
      const at = new THREE.Vector3(p.x + 0.45, 1.05, p.z + 0.85);
      this.houseAt.push(at);
      this.houseLamps.push(this.light(at, '#ffb070', 2.2, 0.75, 0.7));
      // A water barrel and a stack of cut stone by the door.
      kit.add(cyl, WOOD[1] as string, place(p.x - 0.62, 0.3, p.z + 0.95, 0, new THREE.Vector3(0.36, 0.6, 0.36)));
      kit.add(box, '#9aa0a8', place(p.x + 0.75, 0.18, p.z + 1.05, 0.3, new THREE.Vector3(0.4, 0.3, 0.3)));
    });
    // Meltwater: channels of pale ice down the wall, puddles on the ledge, frost along the wall's foot.
    for (let i = 0; i < 6; i++) {
      const x = -6.5 + i * 2.6 + Math.sin(i * 3.1) * 0.5;
      kit.add(box, '#6f97b0', place(x, 4.5, wz + 0.15, 0, new THREE.Vector3(0.07, 9, 0.05), 0, Math.sin(i) * 0.05));
      kit.add(box, '#4f6a7c', place(x, 0.02, wz + 1.6, 0, new THREE.Vector3(0.6, 0.02, 0.36)));
    }
    for (let k = 0; k < 10; k++) {
      const h = L.floor[(k * 53) % L.floor.length];
      if (!h || h.r < -3) continue;
      const p = this.hex(h.q, h.r, 0.01);
      kit.add(new THREE.CircleGeometry(0.32, 10).rotateX(-Math.PI / 2), '#4e6a7e', place(p.x + Math.sin(k) * 0.3, 0.012, p.z + Math.cos(k) * 0.3, 0));
    }
    // The rim along the south: broken edge rocks and the drop below (a dark slope down into mist).
    const R = STRUTS;
    for (let a = 0; a < 26; a++) {
      const x = -9 + a * 0.75;
      const z = this.hex(0, R - 1).z + 0.55 + Math.sin(a * 1.7) * 0.25;
      this.rock(kit, x, z, 0.25 + (Math.sin(a * 2.3) * 0.5 + 0.5) * 0.35, 0.8, a, SLATE);
      far.add(box, '#2c333e', place(x, -2.5, z + 1.6, 0, new THREE.Vector3(0.9, 5, 2.4), 0.3, 0));
    }
    const mist = this.track(new THREE.MeshBasicMaterial({ map: this.track(glowTex('rgba(190,204,226,0.55)', 'rgba(190,204,226,0)')), transparent: true, depthWrite: false, fog: false }));
    for (let k = -2; k <= 2; k++) {
      const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(9, 4).rotateX(-Math.PI / 2)), mist);
      m.position.set(k * 6, -0.6, this.hex(0, R - 1).z + 3.0);
      this.group.add(m);
    }
    // The great stair's landing over the ledge, hanging from its struts: timber deck, stone steps up to the west, chains.
    const landing = new THREE.Group();
    const sx = L.struts.map((h) => this.hex(h.q, h.r, 0));
    const lz = (sx[0] as THREE.Vector3).z - 0.25;
    const x0 = (sx[0] as THREE.Vector3).x - 1.2;
    const x1 = (sx[sx.length - 1] as THREE.Vector3).x + 1.2;
    const deckM = this.std('#8a6a4e', { roughness: 0.9 });
    const beamM = this.std('#6e5440', { roughness: 0.9 });
    const stoneM = this.std('#7c848f', { roughness: 0.92 });
    const iceM = this.std('#cfeaf6', { roughness: 0.15, metalness: 0.1, emissive: new THREE.Color('#1a3a4c') });
    const deck = new THREE.Mesh(this.track(new THREE.BoxGeometry(x1 - x0, 0.22, 1.5)), deckM);
    deck.position.set((x0 + x1) / 2, 2.7, lz - 0.3);
    deck.castShadow = true;
    landing.add(deck);
    for (const dz of [-0.95, 0.4]) {
      const b = new THREE.Mesh(this.track(new THREE.BoxGeometry(x1 - x0 + 0.3, 0.2, 0.2)), beamM);
      b.position.set((x0 + x1) / 2, 2.52, lz - 0.3 + dz);
      landing.add(b);
    }
    // Planks across, a rail along the front.
    for (let x = x0 + 0.3; x < x1; x += 0.55) {
      const pl = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.05, 0.02, 1.48)), beamM);
      pl.position.set(x, 2.82, lz - 0.3);
      landing.add(pl);
    }
    for (let x = x0 + 0.2; x <= x1; x += 1.2) {
      const post = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 0.6, 0.08)), beamM);
      post.position.set(x, 3.1, lz + 0.42);
      landing.add(post);
    }
    const rail = new THREE.Mesh(this.track(new THREE.BoxGeometry(x1 - x0, 0.07, 0.07)), beamM);
    rail.position.set((x0 + x1) / 2, 3.38, lz + 0.42);
    landing.add(rail);
    // The steps climbing the wall westward, one broken off.
    for (let k = 0; k < 7; k++) {
      if (k === 3) continue;
      const st = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.6, 0.22, 1.3)), stoneM);
      st.position.set(x0 - 0.3 - k * 0.55, 2.9 + k * 0.36, lz - 0.6);
      landing.add(st);
    }
    // Icicles under the deck, chains up to the wall.
    for (let x = x0 + 0.4; x < x1; x += 0.7) {
      const ic = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.06, 0.4 + Math.abs(Math.sin(x * 3.1)) * 0.45, 5)), iceM);
      ic.position.set(x, 2.25, lz + 0.35);
      ic.rotation.x = Math.PI;
      landing.add(ic);
    }
    for (const x of [x0 + 0.5, x1 - 0.5]) {
      const ch = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.03, 0.03, 3.4, 5)), this.std('#3a3f46', { roughness: 0.4, metalness: 0.7 }));
      ch.position.set(x, 4.3, lz - 0.7);
      ch.rotation.x = 0.45;
      landing.add(ch);
    }
    this.group.add(landing);
    this.landing = landing;
    // The struts: a timber post from the ledge up under the deck, slanted, a brace, an iron shoe; wedges at the foot.
    const postM = this.std('#b08a66', { roughness: 0.85 });
    const shoeM = this.std('#5a616b', { roughness: 0.4, metalness: 0.6 });
    const wedgeM = this.std('#d9c4a6', { roughness: 0.85 });
    L.struts.forEach((h, i) => {
      const p = this.hex(h.q, h.r, 0);
      const g = new THREE.Group();
      g.position.set(p.x, 0, p.z);
      const post = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.4, 2.7, 0.4)), postM);
      post.position.set(0, 1.3, -0.15);
      post.rotation.x = -0.12;
      post.castShadow = true;
      g.add(post);
      const brace = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.12, 1.4, 0.12)), postM);
      brace.position.set(0.3, 0.9, -0.1);
      brace.rotation.z = 0.5;
      g.add(brace);
      const shoe = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.4, 0.16, 0.4)), shoeM);
      shoe.position.set(0, 0.08, 0);
      g.add(shoe);
      const cap = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.36, 0.14, 0.36)), shoeM);
      cap.position.set(0, 2.55, -0.3);
      g.add(cap);
      const wedges: THREE.Mesh[] = [];
      for (const k of [-1, 1]) {
        const w = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.18, 0.28, 0.55)), wedgeM);
        w.position.set(k * 0.34, 0.14, 0);
        w.rotation.z = k * 0.35;
        w.visible = false;
        g.add(w);
        wedges.push(w);
      }
      this.group.add(g);
      this.struts.push({ g, wedges, at: new THREE.Vector3(p.x, 0.1, p.z), shake: 0, flash: 0 });
      void i;
    });
    // The moon's cold light over the ledge; lanterns along it.
    for (const x of [-5.5, 0.2, 5.6]) this.light(new THREE.Vector3(x, 1.2, this.hex(0, 1).z), '#ffb07a', 2.4, 0.38, 0.6);
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the mine ---------- */

  /** A cart: a timber tub banded in iron on four iron wheels. */
  private cartModel(): CartView {
    const g = new THREE.Group();
    const wood = this.std('#7e5e44', { roughness: 0.9 });
    const iron = this.std('#59606a', { roughness: 0.4, metalness: 0.65 });
    const tub = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.9, 0.5, 0.66)), wood);
    tub.position.y = 0.48;
    tub.castShadow = true;
    g.add(tub);
    for (const y of [0.3, 0.66]) {
      const b = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.94, 0.05, 0.7)), iron);
      b.position.y = y;
      g.add(b);
    }
    const ore = new THREE.Mesh(this.track(new THREE.DodecahedronGeometry(0.28, 0)), this.std('#3e4652', { roughness: 0.9 }));
    ore.position.set(0, 0.74, 0);
    ore.scale.set(1.4, 0.6, 1.1);
    g.add(ore);
    const wheels: THREE.Mesh[] = [];
    for (const x of [-0.3, 0.3])
      for (const z of [-0.34, 0.34]) {
        const w = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.15, 0.15, 0.08, 10)), iron);
        w.rotation.x = Math.PI / 2;
        w.position.set(x, 0.15, z);
        g.add(w);
        wheels.push(w);
      }
    const frost = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.0, 0.62, 0.76)), this.std('#d8f0fa', { roughness: 0.2, transparent: true, opacity: 0.55, emissive: new THREE.Color('#2a5a74') }));
    frost.position.y = 0.48;
    g.add(frost);
    // The frame of new planks while the next is built.
    const frame = new THREE.Group();
    for (const x of [-0.4, 0.4])
      for (const z of [-0.3, 0.3]) {
        const p = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.06, 0.6, 0.06)), this.std('#c8a47c', { roughness: 0.9 }));
        p.position.set(x, 0.3, z);
        frame.add(p);
      }
    frame.visible = false;
    this.group.add(g);
    this.group.add(frame);
    return { g, frost, wheels, frame, pos: new THREE.Vector3(), spin: 0 };
  }

  private buildMine(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = cartsLayout(s.seed);
    this.M = L;
    this.discColor = COLD;
    this.setOpen(L.floor);
    this.underfloor(far, -0.6, '#141c26');
    this.wallsRound(kit, far, hot, true, 1.35);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const ironC = '#5a616b';
    // The rails: sleepers and two iron rails along each one's cells; the slope climbs to the cart's top.
    L.rails.forEach((rl, ri) => {
      const pts = rl.cells.map((h) => this.hex(h.q, h.r, 0));
      const ys = pts.map((_, i) => (i < 3 ? (3 - i) * 0.42 : 0));
      this.slopeY.push(ys);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i] as THREE.Vector3;
        const y = ys[i] as number;
        // A ramp of packed ice and rock under the slope.
        if (y > 0) kit.add(box, ROCKB[i % 4] as string, place(p.x, y / 2 - 0.05, p.z, 0, new THREE.Vector3(S * 1.02, y, S * 0.9)));
        for (let k = -1; k <= 1; k++) kit.add(box, WOOD[(i + k + 3) % 3] as string, place(p.x + k * 0.38, y + 0.04, p.z, 0, new THREE.Vector3(0.14, 0.07, 0.9)));
      }
      for (let i = 0; i + 1 < pts.length; i++) {
        const a = pts[i] as THREE.Vector3;
        const b = pts[i + 1] as THREE.Vector3;
        const ya = ys[i] as number;
        const yb = ys[i + 1] as number;
        const len = Math.hypot(b.x - a.x, yb - ya);
        const ang = Math.atan2(yb - ya, b.x - a.x);
        for (const dz of [-0.28, 0.28]) kit.add(box, ironC, place((a.x + b.x) / 2, (ya + yb) / 2 + 0.11, a.z + dz, 0, new THREE.Vector3(len + 0.05, 0.06, 0.07), 0, ang));
      }
      // The buffer at the far end: a timber stop with iron.
      const end = pts[pts.length - 1] as THREE.Vector3;
      kit.add(box, WOOD[0] as string, place(end.x + rl.side * 0.45 * -1, 0.35, end.z, 0, new THREE.Vector3(0.24, 0.7, 1.0)));
      kit.add(box, ironC, place(end.x - rl.side * 0.5, 0.45, end.z, 0, new THREE.Vector3(0.1, 0.18, 0.9)));
      // The cart and its brake.
      const cv = this.cartModel();
      this.carts.push(cv);
      const bp = this.hex(rl.brake.q, rl.brake.r, 0);
      kit.add(box, ROCKB[1] as string, place(bp.x, 0.15, bp.z, 0, new THREE.Vector3(0.6, 0.3, 0.6)));
      const lever = new THREE.Group();
      lever.position.set(bp.x, 0.3, bp.z);
      const bar = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.09, 1.3, 0.09)), this.std('#6a717b', { roughness: 0.4, metalness: 0.6 }));
      bar.position.y = 0.65;
      lever.add(bar);
      const grip = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.13, 8, 6)), this.std('#d23a24', { roughness: 0.4, emissive: new THREE.Color('#5a120a') }));
      grip.position.y = 1.32;
      lever.add(grip);
      lever.rotation.z = rl.side * -0.45;
      this.group.add(lever);
      this.brakes.push(lever);
      this.brakePull.push(0);
      this.light(new THREE.Vector3(bp.x, 1.2, bp.z), '#ffb48a', 2.0, 0.45, 0.6);
      void ri;
    });
    // Cold lamps along the gallery every few rows, on the walls.
    L.way.forEach((w, i) => {
      if (i % 3 !== 1) return;
      const side = i % 2 ? 1 : -1;
      const p = this.ctx.at(w.x + side * 1850, w.y, 0);
      kit.add(box, IRON, place(p.x, 1.5, p.z, 0, new THREE.Vector3(0.18, 0.26, 0.18)));
      hot.add(box, '#d6f4ff', place(p.x, 1.5, p.z, 0, new THREE.Vector3(0.11, 0.16, 0.11)));
      this.light(new THREE.Vector3(p.x - side * 0.2, 1.4, p.z), COLD, 2.6, 0.5, 0.3);
    });
    // The drill hall's door at the top: two pillars, a lintel with the clan's runes, an iron door ajar, warm light.
    const d = this.ctx.at(L.door.x, L.door.y, 0);
    for (const k of [-1, 1]) {
      kit.add(box, '#7c848f', place(d.x + k * 1.6, 1.4, d.z - 0.8, 0, new THREE.Vector3(0.7, 2.8, 0.7)));
      kit.add(box, '#6a727d', place(d.x + k * 1.6, 2.85, d.z - 0.8, 0, new THREE.Vector3(0.9, 0.18, 0.9)));
    }
    kit.add(box, '#606873', place(d.x, 3.1, d.z - 0.8, 0, new THREE.Vector3(4.0, 0.45, 0.8)));
    for (let k = -2; k <= 2; k++) hot.add(box, '#9fdcff', place(d.x + k * 0.5, 3.1, d.z - 0.38, 0, new THREE.Vector3(0.12, 0.2, 0.02)));
    kit.add(box, '#1a222c', place(d.x, 1.3, d.z - 1.1, 0, new THREE.Vector3(2.5, 2.6, 0.2)));
    kit.add(box, '#4a5058', place(d.x + 0.9, 1.3, d.z - 0.9, 0.6, new THREE.Vector3(1.2, 2.5, 0.1)));
    this.light(new THREE.Vector3(d.x, 1.2, d.z - 0.6), '#ffb48a', 4.2, 0.55, 0.3);
    // The way in at the foot: the glacier's tongue, pale and bright.
    const st = this.ctx.at(L.start.x, L.start.y, 0);
    far.add(box, '#cfe6f2', place(st.x, -0.2, st.z + 3, 0, new THREE.Vector3(8, 0.4, 4)));
    this.light(new THREE.Vector3(st.x, 0.6, st.z + 1.6), '#e8f6ff', 4.5, 0.4, 0);
    box.dispose();
  }

  /* ---------- the drill hall ---------- */

  private buildHall(kit: Kit, far: Kit, hot: Kit, s: BattleState): void {
    const L = drillsLayout(s.seed);
    this.D = L;
    this.discColor = COLD;
    this.setOpen([...L.floor, ...L.block, ...L.drills, L.mill]);
    this.underfloor(far, -0.6, '#141c26');
    this.wallsRound(kit, far, hot, true, 2.3);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cyl = new THREE.CylinderGeometry(0.5, 0.5, 1, 12);
    // The block of ice with the geode: a core, and its north and south faces as slabs of chunks that can fall.
    const bc = L.block.map((h) => this.hex(h.q, h.r, 0));
    const minX = Math.min(...bc.map((p) => p.x)) - 0.55;
    const maxX = Math.max(...bc.map((p) => p.x)) + 0.55;
    const minZ = Math.min(...bc.map((p) => p.z)) - 0.45;
    const maxZ = Math.max(...bc.map((p) => p.z)) + 0.45;
    const cx = (minX + maxX) / 2;
    const cz = (minZ + maxZ) / 2;
    this.hallC.set(cx, 0, cz);
    const iceCore = this.std('#7fb6d6', { roughness: 0.12, metalness: 0.05, transparent: true, opacity: 0.86, emissive: new THREE.Color('#10283a') });
    const core = new THREE.Mesh(this.track(new THREE.BoxGeometry(maxX - minX - 0.2, 2.4, maxZ - minZ - 1.0)), iceCore);
    core.position.set(cx, 1.2, cz);
    this.group.add(core);
    // The geode: a ring of blue crystals round a pale heart, glowing.
    const gm = this.track(new THREE.MeshBasicMaterial({ color: '#5fc8ff', fog: false }));
    this.geodeMat = gm;
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      const c = new THREE.Mesh(this.track(new THREE.OctahedronGeometry(0.22, 0)), gm);
      c.position.set(cx + Math.cos(a) * 0.42, 1.2 + Math.sin(a) * 0.4, cz);
      c.scale.set(0.6, 1.4, 0.6);
      c.rotation.z = a;
      this.group.add(c);
    }
    const heart = new THREE.Mesh(this.track(new THREE.OctahedronGeometry(0.3, 0)), this.track(new THREE.MeshBasicMaterial({ color: '#e6f8ff', fog: false })));
    heart.position.set(cx, 1.2, cz);
    this.group.add(heart);
    this.geode = new THREE.Vector3(cx, 1.2, cz);
    const faceM = this.std('#a7d6ee', { roughness: 0.2, metalness: 0.05, emissive: new THREE.Color('#163448') });
    for (const w of [0, 1]) {
      const z = w === 0 ? minZ + 0.25 : maxZ - 0.25;
      const crack = this.track(new THREE.MeshBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0, depthWrite: false, fog: false }));
      this.crackMats.push(crack);
      for (let ix = 0; ix < 4; ix++)
        for (let iy = 0; iy < 2; iy++) {
          const wx = (maxX - minX) / 4;
          const m = new THREE.Mesh(this.track(new THREE.BoxGeometry(wx - 0.04, 1.2 - 0.04, 0.5)), faceM);
          const home = new THREE.Vector3(minX + wx * (ix + 0.5), 0.6 + iy * 1.2, z);
          m.position.copy(home);
          m.castShadow = true;
          this.group.add(m);
          // Its cracks: white lines across it, showing as it is cut.
          const cr = new THREE.Mesh(this.track(new THREE.PlaneGeometry(wx * 0.9, 0.04)), crack);
          cr.position.set(0, (ix + iy) % 2 ? 0.15 : -0.2, w === 0 ? -0.26 : 0.26);
          cr.rotation.z = ((ix * 3 + iy) % 5) * 0.4 - 0.8;
          if (w === 0) cr.rotation.y = Math.PI;
          m.add(cr);
          const cr2 = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.04, 0.9)), crack);
          cr2.position.set((ix % 2 ? 0.2 : -0.25) * wx, 0, w === 0 ? -0.26 : 0.26);
          cr2.rotation.z = 0.3;
          if (w === 0) cr2.rotation.y = Math.PI;
          m.add(cr2);
          (this.wallChunks[w] as typeof this.wallChunks[number]).push({ mesh: m, home, fall: new THREE.Vector3((ix - 1.5) * 0.6, 0, w === 0 ? -1.2 : 1.2), spin: (ix - 1.5) * 0.8 });
        }
    }
    // The roof's shaft: an iron rod along the hall at height, on hangers, pulleys over each drill.
    const shaftY = 4.4;
    // (The mill out at the mouth, a little beyond the station so it never covers its levers.)
    const millP = this.hex(L.mill.q, L.mill.r, 0).add(new THREE.Vector3(-1.0, 0, 1.4));
    kit.add(cyl, '#5a616b', place((millP.x + 4) / 2, shaftY, cz - 0.1, 0, new THREE.Vector3(0.16, Math.abs(4 - millP.x) + 2, 0.16), 0, Math.PI / 2));
    for (let x = millP.x; x <= 4; x += 2.2) {
      kit.add(box, IRON, place(x, shaftY + 0.6, cz - 0.1, 0, new THREE.Vector3(0.08, 1.2, 0.08)));
    }
    // The mill at the mouth: a great wheel of sails facing out (south), on a timber tower.
    kit.add(box, WOOD[1] as string, place(millP.x, 1.6, millP.z, 0, new THREE.Vector3(0.6, 3.2, 0.6)));
    kit.add(box, IRON, place(millP.x, 3.3, millP.z, 0, new THREE.Vector3(0.4, 0.4, 0.8)));
    const mill = new THREE.Group();
    mill.position.set(millP.x, 3.3, millP.z + 0.55);
    const sailM = this.std('#c9d4dc', { roughness: 0.8, side: THREE.DoubleSide });
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      const arm = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 2.0, 0.06)), this.std('#7a5c42', { roughness: 0.9 }));
      arm.position.set(Math.sin(a) * 1.0, Math.cos(a) * 1.0, 0);
      arm.rotation.z = -a;
      mill.add(arm);
      const sail = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.5, 1.4)), sailM);
      sail.position.set(Math.sin(a) * 1.15 + Math.cos(a) * 0.25, Math.cos(a) * 1.15 - Math.sin(a) * 0.25, 0.02);
      sail.rotation.z = -a;
      mill.add(sail);
    }
    this.group.add(mill);
    this.mill = mill;
    // The drills: frame, belt from the roof, auger aimed at its face, its force and arrow, its clutch lever.
    const plateM = (n: number): THREE.MeshBasicMaterial => this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(String(n))), transparent: true, depthWrite: false }));
    const arrowM = this.track(new THREE.MeshBasicMaterial({ map: this.track(arrowTex()), transparent: true, depthWrite: false, side: THREE.DoubleSide, fog: false }));
    const frameM = this.std('#9a7656', { roughness: 0.88 });
    const ironM = this.std('#7a818c', { roughness: 0.35, metalness: 0.65 });
    const bitM = this.std('#b8c0ca', { roughness: 0.25, metalness: 0.8 });
    const gripM = this.std('#d23a24', { roughness: 0.4, emissive: new THREE.Color('#5a120a') });
    const beltM = this.std('#3a2e26', { roughness: 0.95 });
    L.drills.forEach((h, d) => {
      const p = this.hex(h.q, h.r, 0);
      const north = L.set.wall[d] === 0;
      const toward = north ? 1 : -1;
      const g = new THREE.Group();
      g.position.set(p.x, 0, p.z);
      for (const k of [-1, 1]) {
        const post = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.2, 2.2, 0.2)), frameM);
        post.position.set(k * 0.5, 1.1, 0);
        g.add(post);
      }
      const top = new THREE.Mesh(this.track(new THREE.BoxGeometry(1.25, 0.18, 0.3)), frameM);
      top.position.set(0, 2.2, 0);
      g.add(top);
      const housing = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.34, 0.34, 0.5, 12)), ironM);
      housing.rotation.x = Math.PI / 2;
      housing.position.set(0, 1.15, 0);
      g.add(housing);
      // The auger: a long cone with flutes, along z toward the ice.
      const auger = new THREE.Group();
      auger.position.set(0, 1.15, toward * 0.25);
      const cone = new THREE.Mesh(this.track(new THREE.ConeGeometry(0.24, 1.1, 8)), bitM);
      cone.rotation.x = toward * (Math.PI / 2);
      cone.position.z = toward * 0.55;
      auger.add(cone);
      for (let i = 0; i < 4; i++) {
        const fl = new THREE.Mesh(this.track(new THREE.TorusGeometry(0.2 - i * 0.04, 0.025, 3, 10)), ironM);
        fl.position.z = toward * (0.2 + i * 0.22);
        fl.rotation.y = 0.35;
        auger.add(fl);
      }
      // A stripe on it so its turn reads.
      const stripe = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.05, 0.05, 0.9)), gripM);
      stripe.position.set(0.17, 0, toward * 0.45);
      auger.add(stripe);
      g.add(auger);
      // The belt down from the roof to its pulley.
      const belt = new THREE.Group();
      const pul = new THREE.Mesh(this.track(new THREE.CylinderGeometry(0.22, 0.22, 0.12, 12)), ironM);
      pul.rotation.z = Math.PI / 2;
      pul.position.set(0, 1.75, -toward * 0.05);
      g.add(pul);
      const strap = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.1, shaftY - 1.75, 0.05)), beltM);
      strap.position.set(0, (shaftY + 1.75) / 2, cz - 0.1 - p.z);
      belt.add(strap);
      for (let i = 0; i < 4; i++) {
        const mk = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.12, 0.08, 0.06)), this.std('#b0a090', { roughness: 0.9 }));
        mk.position.set(0, 2.0 + i * 0.6, cz - 0.1 - p.z);
        belt.add(mk);
      }
      g.add(belt);
      // The force plate and the arrow, facing the hall's mouth (south), big.
      const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.62, 0.62)), plateM(L.set.force[d] as number));
      plate.position.set(0, 2.62, 0.12);
      plate.rotation.x = -0.3;
      g.add(plate);
      const arrow = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.7, 0.7)), arrowM);
      arrow.position.set(0.0, 3.25, 0.12);
      arrow.rotation.x = -0.3;
      // (The way it cuts, as seen from the hall: mirrored for one hand.)
      if (L.set.right0[d] !== north) arrow.scale.x = -1;
      g.add(arrow);
      this.group.add(g);
      // Its clutch lever at its spot.
      const sp = this.hex((L.spots[d] as { q: number }).q, (L.spots[d] as { r: number }).r, 0);
      kit.add(box, '#5a616b', place(sp.x + 0.35, 0.12, sp.z, 0, new THREE.Vector3(0.36, 0.24, 0.36)));
      const lever = new THREE.Group();
      lever.position.set(sp.x + 0.35, 0.24, sp.z);
      const bar = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 1.0, 0.08)), ironM);
      bar.position.y = 0.5;
      lever.add(bar);
      const grip = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.12, 8, 6)), gripM);
      grip.position.y = 1.0;
      lever.add(grip);
      this.group.add(lever);
      this.drills.push({ g, auger, lever, belt, tip: new THREE.Vector3(p.x, 1.15, p.z + toward * 1.2), spin: 0, flick: 0 });
      this.light(new THREE.Vector3(p.x, 2.4, p.z + 0.4), '#ffd8c4', 1.8, 0.3, 0.3);
    });
    // The belts' station: two pulleys a belt, straight or crossed; their levers I and II with plates.
    L.levers.forEach((h, b) => {
      const p = this.hex(h.q, h.r, 0);
      kit.add(box, WOOD[1] as string, place(p.x, 1.4, p.z - 0.45, 0, new THREE.Vector3(0.16, 2.8, 0.16)));
      kit.add(cyl, '#7a818c', place(p.x, 2.7, p.z - 0.35, 0, new THREE.Vector3(0.5, 0.1, 0.5), Math.PI / 2, 0));
      kit.add(cyl, '#7a818c', place(p.x, 0.9, p.z - 0.35, 0, new THREE.Vector3(0.5, 0.1, 0.5), Math.PI / 2, 0));
      const mk = (cross: boolean): THREE.Group => {
        const gg = new THREE.Group();
        for (const k of [-1, 1]) {
          const st = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.07, 1.85, 0.04)), beltM);
          st.position.set(cross ? 0 : k * 0.24, 1.8, p.z - 0.3 - p.z);
          if (cross) st.rotation.z = k * 0.26;
          gg.add(st);
        }
        gg.position.set(p.x, 0, p.z);
        this.group.add(gg);
        return gg;
      };
      const straight = mk(false);
      const crossed = mk(true);
      const plate = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.5, 0.5)), this.track(new THREE.MeshBasicMaterial({ map: this.track(numTex(b === 0 ? 'I' : 'II')), transparent: true, depthWrite: false })));
      plate.position.set(p.x, 3.1, p.z - 0.3);
      plate.rotation.x = -0.25;
      this.group.add(plate);
      const lever = new THREE.Group();
      lever.position.set(p.x + 0.35, 0.2, p.z + 0.1);
      const bar = new THREE.Mesh(this.track(new THREE.BoxGeometry(0.08, 1.1, 0.08)), ironM);
      bar.position.y = 0.55;
      lever.add(bar);
      const grip = new THREE.Mesh(this.track(new THREE.SphereGeometry(0.13, 8, 6)), gripM);
      grip.position.y = 1.1;
      lever.add(grip);
      this.group.add(lever);
      this.beltViews.push({ straight, crossed, lever });
    });
    // La Barrena's slash arc and its spin ring.
    const arcM = this.track(new THREE.MeshBasicMaterial({ color: RED, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide }));
    const arcA = (SENDA.drills.boss.arc / 1024) * Math.PI * 2;
    const arc = new THREE.Mesh(this.track(new THREE.RingGeometry(0.4, (SENDA.drills.boss.reach / 1000) * S, 24, 1, -arcA, arcA * 2).rotateX(-Math.PI / 2)), arcM);
    arc.renderOrder = 3;
    arc.visible = false;
    this.group.add(arc);
    this.arc = arc;
    this.arcMat = arcM;
    const spM = this.track(new THREE.MeshBasicMaterial({ color: COLD, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending }));
    const ring = new THREE.Mesh(this.track(new THREE.RingGeometry(1.2, 8.2, 48).rotateX(-Math.PI / 2)), spM);
    ring.position.set(0, 0.05, 0);
    ring.renderOrder = 2;
    this.group.add(ring);
    this.spinRing = ring;
    this.spinMat = spM;
    // The broken drills' heap, where La Barrena comes from.
    const hp = this.ctx.at(L.heap.x, L.heap.y, 0);
    for (let i = 0; i < 7; i++) kit.add(i % 2 ? cyl : box, i % 3 ? '#5a616b' : '#6a4a3a', place(hp.x + Math.sin(i * 2.1) * 0.6, 0.15 + (i % 3) * 0.18, hp.z + Math.cos(i * 1.7) * 0.5, i, new THREE.Vector3(0.7, 0.2, 0.25), i * 0.4, i * 0.3));
    // Cold lamps on the walls, warm ones by the drills.
    for (const [q, r] of [
      [-5, 0],
      [5, -1],
      [-2, -4],
      [3, -4],
      [4, 2],
    ] as const) {
      const p = this.hex(q, r, 0);
      kit.add(box, IRON, place(p.x, 2.0, p.z, 0, new THREE.Vector3(0.18, 0.26, 0.18)));
      hot.add(box, '#d6f4ff', place(p.x, 2.0, p.z, 0, new THREE.Vector3(0.11, 0.16, 0.11)));
      this.light(new THREE.Vector3(p.x, 1.9, p.z), COLD, 3.0, 0.45, 0.3);
    }
    this.light(new THREE.Vector3(cx, 1.2, cz), '#5fc8ff', 2.4, 0.3, 0.2);
    box.dispose();
    cyl.dispose();
  }

  /* ---------- the camp ---------- */

  private buildCamp(kit: Kit, far: Kit, hot: Kit): void {
    const R = 5;
    const cells: { q: number; r: number }[] = [];
    for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (Math.max(Math.abs(q), Math.abs(r), Math.abs(q + r)) <= 3) cells.push({ q, r });
    this.setOpen(cells);
    this.underfloor(far, -0.6, '#141c26');
    this.wallsRound(kit, far, hot, true, 2.4);
    const box = new THREE.BoxGeometry(1, 1, 1);
    // The overturned cart: its tub on its side, wheels up, ore spilled.
    const c = this.hex(1, -1, 0);
    kit.add(box, '#7e5e44', place(c.x, 0.34, c.z, 0.4, new THREE.Vector3(0.9, 0.66, 0.5), 0, Math.PI / 2 - 0.2));
    kit.add(box, '#59606a', place(c.x, 0.34, c.z, 0.4, new THREE.Vector3(0.95, 0.7, 0.06), 0, Math.PI / 2 - 0.2));
    for (const k of [-1, 1]) kit.add(new THREE.CylinderGeometry(0.15, 0.15, 0.08, 10), '#59606a', place(c.x + 0.5, 0.5 + k * 0.25, c.z + k * 0.2, 0.4, 1, Math.PI / 2, 0));
    for (let i = 0; i < 5; i++) kit.add(new THREE.DodecahedronGeometry(0.16, 0), '#3e4652', place(c.x - 0.6 + i * 0.25, 0.08, c.z + 0.5 + Math.sin(i) * 0.2, i));
    // A rail running past.
    for (let x = -4; x <= 4; x += 0.7) kit.add(box, WOOD[1] as string, place(x, 0.03, c.z - 1.2, 0, new THREE.Vector3(0.14, 0.06, 0.9)));
    for (const dz of [-0.28, 0.28]) kit.add(box, '#5a616b', place(0, 0.09, c.z - 1.2 + dz, 0, new THREE.Vector3(8.5, 0.06, 0.07)));
    this.light(new THREE.Vector3(0, 0.4, 0), '#ff9a5a', 3.5, 0.45, 0.6);
    box.dispose();
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild, sky: Sky = 'night'): void {
    this.sky = sky;
    const c = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      const grada = this.place === 'gradaThaw';
      const floor = grada ? ['#4c535e', '#444b56', '#555d68'] : ['#5f7686', '#566c7c', '#687f8f'];
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const lum = col.getX(i) * 0.3 + col.getY(i) * 0.59 + col.getZ(i) * 0.11;
        const k = 0.5 + 0.5 * Math.sin(v.x * 1.7 + v.z * 0.9) * Math.cos(v.z * 1.3 - v.x * 0.5);
        c.set(floor[Math.floor(k * 2.99)] as string).multiplyScalar(0.85 + lum * 0.3);
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    if (terrain.fog) terrain.fog.group.visible = false;
    if (this.outside) {
      this.dome = new SkyDome();
      this.group.add(this.dome.mesh);
    }
    this.paint(sky);
  }

  paint(sky: Sky = this.sky): void {
    this.sky = sky;
    const look = this.place === 'gradaThaw' ? (sky === 'dawn' || sky === 'day' ? LOOK.dawn : LOOK.night) : this.place === 'mineCamp' ? LOOK.camp : LOOK.ice;
    if (!look) return;
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    if (this.dome) {
      if (sky === 'dawn' || sky === 'day') {
        this.dome.set('#4c5a78', '#c6b8b4', '#ffd8c0');
        this.dome.u.uSunDir.value.set(0.7, 0.15, -0.6);
      } else {
        this.dome.set('#121a2c', '#3a4660', '#e8f0ff');
        this.dome.u.uSunDir.value.set(-0.4, 0.55, -0.7);
      }
    }
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const at = (h = 0.2): THREE.Vector3 => this.ctx.at(e.x, e.y, h);
      if (e.kind === 'sway') {
        this.jolt = 1;
        this.ctx.shake(0.5);
        const sv = this.struts[e.n ?? 0];
        if (sv) {
          sv.shake = 1;
          this.ctx.vfx.burst(sv.at.clone().setY(2.5), { n: 22, color: '#a0a8b2', speed: 1.4, up: -0.5, size: 0.14, life: 18, gravity: 1, spread: 1.5 });
        }
      } else if (e.kind === 'golemLet') {
        const sv = this.struts[e.n ?? 0];
        if (sv) sv.shake = 1;
      } else if (e.kind === 'golemSet') {
        const sv = this.struts[e.n ?? 0];
        if (sv) this.ctx.vfx.ring(sv.at.clone(), 0.2, 1.0, '#ffd8c4', 8, 0.6);
      } else if (e.kind === 'wedged') {
        const sv = this.struts[e.n ?? 0];
        if (sv) {
          sv.flash = 1;
          this.ctx.flare(sv.at.clone().setY(1.0), '#ffffff', 3.5, 14);
          this.ctx.vfx.burst(sv.at.clone().setY(0.3), { n: 24, color: '#e8d6bc', speed: 2.2, up: 2.0, size: 0.1, life: 12, gravity: 1 });
          this.ctx.vfx.ring(sv.at.clone().setY(0.06), 0.3, 1.8, '#ffffff', 10, 0.9);
        }
      } else if (e.kind === 'free' && this.place === 'gradaThaw') {
        this.ctx.shake(0.3);
        for (const sv of this.struts) this.ctx.flare(sv.at.clone().setY(2.4), '#ffffff', 3, 18);
      } else if (e.kind === 'houseIn') {
        // The lantern of the nearest house goes out; smoke from its door.
        const p = at(1.0);
        let best = -1;
        let bd = 1e9;
        this.houseAt.forEach((h, i) => {
          const d = h.distanceToSquared(p);
          const l = this.lights[this.houseLamps[i] as number];
          if (l && !l.out && d < bd) {
            bd = d;
            best = i;
          }
        });
        const l = best >= 0 ? this.lights[this.houseLamps[best] as number] : undefined;
        if (l) l.out = true;
        this.ctx.vfx.burst(p, { n: 20, color: '#3a3a40', speed: 1.0, up: 1.6, size: 0.5, life: 28, smoke: true });
        this.ctx.shake(0.2);
      } else if (e.kind === 'holeOut') {
        this.ctx.vfx.burst(at(0.6), { n: 18, color: this.place === 'gradaThaw' ? '#4a4256' : '#5a6878', speed: 1.4, up: 1.2, size: 0.4, life: 22, smoke: true });
      } else if (e.kind === 'pulseWarn') {
        this.ctx.vfx.burst(at(0.1), { n: 6, color: '#d6f4ff', speed: 0.5, up: 0.8, size: 0.06, life: 14 });
      } else if (e.kind === 'pulse') {
        this.ctx.shake(0.22);
        this.ctx.vfx.burst(at(0.2), { n: 28, color: '#d6f4ff', speed: 2.4, up: 2.6, size: 0.14, life: 16, gravity: 1 });
        this.ctx.flare(at(0.4), '#bfe9ff', 2.6, 10);
      } else if (e.kind === 'brake') {
        const i = e.n ?? -1;
        if (i >= 0) this.brakePull[i] = 1;
      } else if (e.kind === 'cartGo') {
        this.ctx.vfx.burst(at(0.8), { n: 30, color: '#e8f6ff', speed: 2.0, up: 1.6, size: 0.12, life: 14, gravity: 1 });
        this.ctx.shake(0.2);
      } else if (e.kind === 'cartHit') {
        this.ctx.shake(0.35);
        this.ctx.vfx.burst(at(0.6), { n: 22, color: '#ffd0a0', speed: 2.6, up: 1.8, size: 0.08, life: 10, gravity: 1 });
      } else if (e.kind === 'shellCrack') {
        this.ctx.shake(0.5);
        this.ctx.flare(at(0.6), '#e8f6ff', 3.4, 14);
        this.ctx.vfx.burst(at(0.5), { n: 30, color: '#2a3a5a', speed: 2.4, up: 2.2, size: 0.16, life: 16, gravity: 1.2 });
        this.ctx.vfx.ring(at(0.06), 0.3, 1.8, '#9fdcff', 10, 0.9);
      } else if (e.kind === 'cartSpent') {
        this.ctx.vfx.burst(at(0.4), { n: 16, color: '#8a7a6a', speed: 1.6, up: 1.4, size: 0.12, life: 14, gravity: 1 });
      } else if (e.kind === 'cartBuilt') {
        this.ctx.vfx.ring(at(0.6), 0.2, 1.2, '#ffd8c4', 10, 0.7);
      } else if (e.kind === 'slamWarn' || e.kind === 'bzWarn') {
        this.ctx.vfx.burst(at(0.2), { n: 8, color: '#9fdcff', speed: 0.6, up: 1.0, size: 0.08, life: 12 });
      } else if (e.kind === 'slam' || e.kind === 'bzSlash') {
        this.ctx.shake(0.4);
        this.ctx.vfx.burst(at(0.3), { n: 24, color: '#cfe6f2', speed: 2.6, up: 1.6, size: 0.12, life: 12, gravity: 1 });
      } else if (e.kind === 'mouth') {
        this.ctx.flare(at(1.2), '#ffd8c4', 5, 20);
      } else if (e.kind === 'clutchIn' || e.kind === 'clutchOut' || e.kind === 'belt') {
        this.ctx.vfx.burst(at(1.0), { n: 10, color: '#ffd0a0', speed: 1.4, up: 1.2, size: 0.06, life: 8, gravity: 1 });
      } else if (e.kind === 'jam') {
        this.jamFlash = 1;
        this.ctx.shake(0.9);
        for (const d of this.drills) this.ctx.vfx.burst(d.g.position.clone().setY(2.2), { n: 18, color: '#ffd0a0', speed: 3, up: 1.6, size: 0.08, life: 10, gravity: 1 });
        this.ctx.flare(new THREE.Vector3(0, 4.4, this.hallC.z), '#ffffff', 5, 14);
      } else if (e.kind === 'thaw') {
        this.ctx.vfx.burst(at(0.6), { n: 26, color: '#cfe6f2', speed: 2.0, up: 2.4, size: 0.14, life: 18, gravity: 1 });
      } else if (e.kind === 'drillStall') {
        const d = this.drills[e.n ?? 0];
        if (d) this.ctx.vfx.burst(d.tip.clone(), { n: 24, color: '#9fdcff', speed: 2.0, up: 1.6, size: 0.1, life: 14, gravity: 0.8 });
        this.ctx.shake(0.3);
      } else if (e.kind === 'wallDown') {
        const w = e.n ?? 0;
        this.wallFlash[w] = 1;
        this.ctx.shake(1.0);
        for (const ch of this.wallChunks[w] ?? []) this.ctx.vfx.burst(ch.home.clone(), { n: 10, color: '#d6f4ff', speed: 2.4, up: 2.0, size: 0.14, life: 18, gravity: 1 });
        if (this.geode) this.ctx.flare(this.geode, '#9fdcff', 6, 26);
      } else if (e.kind === 'bzOut') {
        this.ctx.shake(0.7);
        this.ctx.vfx.burst(at(0.4), { n: 40, color: '#cfe6f2', speed: 3, up: 3, size: 0.16, life: 20, gravity: 0.8 });
      } else if (e.kind === 'bzSpin') {
        this.ctx.shake(0.75);
        for (let k = 0; k < 10; k++) {
          const a = (k / 10) * Math.PI * 2;
          this.ctx.vfx.burst(this.v3.set(Math.cos(a) * 4.5, 0.6, Math.sin(a) * 3.8).clone(), { n: 8, color: '#e8f6ff', speed: 2.4, up: 1.4, size: 0.12, life: 14, gravity: 1 });
        }
      } else if (e.kind === 'bzDown') {
        this.ctx.shake(0.8);
        this.ctx.vfx.burst(at(0.6), { n: 50, color: '#cfe6f2', speed: 3, up: 3, size: 0.18, life: 22, gravity: 0.8 });
      }
    }
  }

  draw(s: BattleState): void {
    this.last = s;
  }

  frame(now: number, camera?: THREE.Camera): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    const s = this.last;
    this.glow.begin();
    if (this.G) this.frameGrada(s, now, dt);
    if (this.M) this.frameMine(s, now, dt);
    if (this.D) this.frameHall(s, now, dt);
    // The spots of ice (cracks, ice off the roof) coming: a pale disc filling as it nears.
    const spots = s?.senda?.carts?.spots ?? s?.senda?.drills?.spots ?? [];
    const warnT = s?.rules?.senda?.carts?.crack.warn ?? s?.rules?.senda?.drills?.pulse.warn ?? 36;
    const rr = ((s?.rules?.senda?.carts?.crack.r ?? s?.rules?.senda?.drills?.pulse.r ?? 1000) / 1000) * S;
    this.discs.forEach((d, i) => {
      const p = spots[i];
      if (!p || !s) {
        d.mesh.visible = false;
        return;
      }
      const left = Math.max(0, (p.t - s.tick) / Math.max(1, warnT));
      d.mesh.visible = true;
      const at = this.ctx.at(p.x, p.y, 0.05);
      d.mesh.position.copy(at);
      d.mesh.scale.setScalar(rr * (0.35 + 0.65 * (1 - left)));
      d.mat.color.copy(this.discColor);
      d.mat.opacity = 0.3 + 0.4 * (1 - left) + 0.15 * Math.sin(now * 16);
      this.glow.add(at.x, 0.2, at.z, COLD, rr * 2.2 * (1 - left * 0.6), 0.3 + 0.3 * (1 - left));
    });
    for (const l of this.lights) {
      if (l.out) continue;
      const f = l.flick ? 0.86 + 0.14 * Math.sin(now * (5 + l.seed) + l.seed) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * f);
    }
    this.glow.end();
  }

  private frameGrada(s: BattleState | null, now: number, dt: number): void {
    const w = s?.senda?.struts;
    const r = s?.rules?.senda?.struts;
    this.jolt = Math.max(0, this.jolt - dt * 2.2);
    if (this.landing) {
      this.landing.position.y = -0.08 * this.jolt * Math.sin(now * 40);
      this.landing.rotation.z = 0.012 * this.jolt * Math.sin(now * 33);
    }
    this.struts.forEach((sv, i) => {
      // (No fight: the scene after it, all four wedged.)
      const st = w ? (w.st[i] ?? 0) : 2;
      const prog = w && r ? Math.min(1, (w.prog[i] ?? 0) / r.caulk) : 1;
      sv.shake = Math.max(0, sv.shake - dt * 2);
      const loose = st === 0 ? 0.035 + 0.02 * Math.sin(now * 2.3 + i) : 0;
      sv.g.rotation.z = (loose + sv.shake * 0.08) * Math.sin(now * (st === 0 ? 7 + i : 30));
      // The wedges going in as Bruni works.
      const show = st === 2 ? 1 : prog;
      for (const wd of sv.wedges) {
        wd.visible = show > 0.02;
        wd.scale.set(1, 1, Math.max(0.1, show));
      }
      sv.flash = Math.max(0, sv.flash - dt * 1.5);
      if (sv.flash > 0) this.glow.add(sv.at.x, 1.2, sv.at.z, WHITE, 2 + 3 * sv.flash, sv.flash);
      if (st === 2) this.glow.add(sv.at.x, 0.2, sv.at.z, WARM, 1.2, 0.35);
      if (st === 1) this.glow.add(sv.at.x, 1.4, sv.at.z, WARM, 1.6, 0.3 + 0.2 * prog);
      // Grillo's lamp on the loosest.
      if (w && w.point === i && st !== 2) {
        const b = 0.5 + 0.5 * Math.sin(now * 6);
        this.glow.add(sv.at.x, 0.15, sv.at.z, RED, 1.8 + b, 0.45 + 0.35 * b);
        this.glow.add(sv.at.x, 1.6, sv.at.z - 0.1, RED, 1.2, 0.25 + 0.25 * b);
      }
    });
  }

  private frameMine(s: BattleState | null, now: number, dt: number): void {
    const L = this.M;
    const r = s?.rules?.senda?.carts;
    const w = s?.senda?.carts;
    if (!L) return;
    this.carts.forEach((cv, i) => {
      const rl = L.rails[i];
      const ys = this.slopeY[i] ?? [];
      if (!rl) return;
      const cart = w?.carts[i];
      const mode = cart?.mode ?? 0;
      const end = (rl.cells.length - 1) * 1000;
      const at = mode === 1 && r ? Math.min(end, cart?.at ?? 0) : mode === 2 ? end : 0;
      const p = r ? cartPoint(r, i, at) : hexToWorld(rl.cells[0]?.q ?? 0, rl.cells[0]?.r ?? 0);
      const wp = this.ctx.at(p.x, p.y, 0);
      const k = at / 1000;
      const i0 = Math.floor(k);
      const y = (ys[i0] ?? 0) + ((ys[i0 + 1] ?? ys[i0] ?? 0) - (ys[i0] ?? 0)) * (k - i0);
      cv.pos.lerp(this.v3.set(wp.x, y + 0.08, wp.z), mode === 1 ? 0.6 : 0.3);
      cv.g.position.copy(cv.pos);
      cv.g.rotation.z = mode === 2 ? rl.side * 1.2 : i0 < 2 ? rl.side * -0.32 : 0;
      cv.frost.visible = mode === 0;
      if (mode === 1) cv.spin += dt * 14;
      for (const wh of cv.wheels) wh.rotation.y = cv.spin;
      if (mode === 1 && Math.sin(now * 30) > 0.5) this.ctx.vfx.burst(cv.pos.clone().setY(0.15), { n: 2, color: '#ffd0a0', speed: 1.8, up: 0.8, size: 0.05, life: 6, gravity: 1 });
      // The next one being built at the top: a frame of new planks.
      const top = this.ctx.at(rl.from.x, rl.from.y, 0);
      cv.frame.visible = mode === 2;
      if (mode === 2 && cart && r) {
        const k2 = 1 - Math.max(0, (cart.t - (s?.tick ?? 0)) / r.again);
        cv.frame.position.set(top.x, (ys[0] ?? 0) + 0.08, top.z);
        cv.frame.scale.set(1, Math.max(0.1, k2), 1);
      }
      if (mode === 0 && w) this.glow.add(cv.pos.x, cv.pos.y + 0.5, cv.pos.z, COLD, 1.4, 0.35 + 0.1 * Math.sin(now * 2 + i));
      // The brake pulled.
      const lever = this.brakes[i];
      const pull = this.brakePull[i] ?? 0;
      if (lever) lever.rotation.z = rl.side * (-0.45 + 0.9 * pull);
      this.brakePull[i] = Math.max(0, pull - dt * 0.7);
      if (mode === 0 && w) {
        const bp = this.hex(rl.brake.q, rl.brake.r, 0);
        this.glow.add(bp.x, 1.6, bp.z, RED, 1.0, 0.35 + 0.25 * Math.sin(now * 4 + i));
      }
    });
    // The beetles still shut in their shells: a frost glint over each.
    for (const b of w?.beetles ?? []) {
      if (!b.shell) continue;
      const p = this.ctx.pos(b.id);
      if (!p) continue;
      this.glow.add(p.x, p.y + 0.9, p.z, COLD, 2.2, 0.45 + 0.15 * Math.sin(now * 3 + b.id));
    }
  }

  private frameHall(s: BattleState | null, now: number, dt: number): void {
    const r = s?.rules?.senda?.drills;
    const w = s?.senda?.drills;
    const L = this.D;
    if (!L) return;
    const tick = s?.tick ?? 0;
    // The mill: turning while the shaft runs, slower with more asked of it; still when it jams.
    const ask = w && r ? r.drills.reduce((a, x, d) => a + (w.inn[d] ? x.force : 0), 0) : 0;
    this.jamFlash = Math.max(0, this.jamFlash - dt * 1.2);
    const speed = this.jamFlash > 0.2 ? 0 : 1.6 - ask * 0.25;
    this.millSpin += dt * speed;
    if (this.mill) this.mill.rotation.z = this.millSpin;
    this.drills.forEach((dv, d) => {
      const inn = !!w?.inn[d];
      const stalled = !!w && (w.stall[d] ?? 0) > tick;
      const right = w && r ? drillTurnsRight(r, w, d) : true;
      const wallDown = w ? (w.down[L.set.wall[d] as number] ?? -1) >= 0 : false;
      const turning = inn && !stalled && this.jamFlash < 0.2;
      if (turning) dv.spin += dt * (right ? 14 : -14);
      dv.auger.rotation.z = dv.spin;
      dv.lever.rotation.x = inn ? 0.9 : -0.5;
      // The belt's marks run while it turns.
      dv.belt.children.forEach((c, i) => {
        if (i === 0) return;
        const k = turning ? (((now * (right ? 1.6 : -1.6) + i * 0.25) % 1) + 1) % 1 : (i - 1) * 0.25;
        c.position.y = 2.0 + k * 2.2;
      });
      const cutting = turning && right && !wallDown && w && r ? wallForce(s as BattleState, r, w, L.set.wall[d] as number) >= r.mill : false;
      if (cutting && Math.sin(now * 25 + d) > 0.3) this.ctx.vfx.burst(dv.tip.clone(), { n: 3, color: '#e8f6ff', speed: 2.2, up: 1.2, size: 0.07, life: 8, gravity: 1 });
      if (cutting) this.glow.add(dv.tip.x, dv.tip.y, dv.tip.z, COLD, 1.8, 0.5);
      if (turning && !right) this.glow.add(dv.tip.x, dv.tip.y, dv.tip.z, RED, 1.4, 0.35 + 0.25 * Math.sin(now * 10));
      if (stalled) this.glow.add(dv.tip.x, dv.tip.y + 0.3, dv.tip.z, COLD, 2.4, 0.55);
      if (inn) this.glow.add(dv.g.position.x, 2.65, dv.g.position.z + 0.15, WARM, 1.0, 0.4);
    });
    // The belts: straight or crossed as they are.
    this.beltViews.forEach((bv, b) => {
      const crossed = (w?.belts[b] ?? 0) === 1;
      bv.straight.visible = !crossed;
      bv.crossed.visible = crossed;
      bv.lever.rotation.x = crossed ? 0.8 : -0.6;
    });
    // The walls: cracks growing with the cut, then down in blocks.
    for (const wall of [0, 1]) {
      const down = w ? (w.down[wall] ?? -1) : -1;
      const k = w && r ? Math.min(1, (w.cut[wall] ?? 0) / r.cut) : 0;
      const cm = this.crackMats[wall];
      if (cm) cm.opacity = down >= 0 ? 0 : k * 0.95;
      this.wallFlash[wall] = Math.max(0, (this.wallFlash[wall] ?? 0) - dt * 0.8);
      for (const ch of this.wallChunks[wall] ?? []) {
        if (down < 0) {
          ch.mesh.visible = true;
          ch.mesh.position.copy(ch.home);
          if (k > 0) ch.mesh.position.x += Math.sin(now * 40 + ch.spin) * 0.015 * k;
          ch.mesh.rotation.set(0, 0, 0);
          continue;
        }
        const t = Math.min(1, (tick - down) / 18);
        ch.mesh.position.set(ch.home.x + ch.fall.x * t, Math.max(0.15, ch.home.y * (1 - t * t)), ch.home.z + ch.fall.z * t);
        ch.mesh.rotation.set(ch.spin * t, 0, ch.spin * 0.6 * t);
      }
      if (k > 0 && down < 0 && this.geode) this.glow.add(this.geode.x, 0.6, this.geode.z + (wall === 0 ? -1.2 : 1.2), COLD, 1.6 * k, 0.3 * k);
    }
    const open = w ? w.down.filter((x) => x >= 0).length : 0;
    if (this.geodeMat) this.geodeMat.color.setRGB(0.37 + 0.2 * open, 0.78 + 0.08 * open, 1);
    if (this.geode) this.glow.add(this.geode.x, this.geode.y, this.geode.z, COLD, 1.6 + open * 0.8 + 0.3 * Math.sin(now * 2), 0.3 + open * 0.12);
    // La Barrena's slash arc, its spin ring.
    const bz = w?.bz;
    const me = bz && bz.id ? this.ctx.pos(bz.id) : undefined;
    if (this.arc && this.arcMat) {
      if (me && bz && bz.wind >= 0 && r) {
        const left = Math.max(0, (bz.wind - tick) / Math.max(1, r.boss.warn));
        this.arc.visible = true;
        this.arc.position.set(me.x, 0.06, me.z);
        this.arc.rotation.y = -(bz.aim / 1024) * Math.PI * 2;
        this.arcMat.opacity = 0.25 + 0.4 * (1 - left) + 0.12 * Math.sin(now * 14);
      } else this.arc.visible = false;
    }
    if (this.spinMat) this.spinMat.opacity = bz && bz.spinAt >= 0 ? 0.22 + 0.22 * Math.sin(now * 12) : 0;
    if (me && bz && bz.mode === 1) this.glow.add(me.x, me.y + 1.3, me.z, COLD, 2.2, 0.4);
  }

  dispose(): void {
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

/** Grada's board radius (the layout's), for the rim. */
const STRUTS = 7;
